import { FC, useEffect, useState } from 'react'
import { ReactDatePickerCustomHeaderProps } from 'react-datepicker'
import styled from 'styled-components'
import LeftIcon from '@tuhi/components/assets/icons/buttons/calendarChevronLeft.svg'
import RightIcon from '@tuhi/components/assets/icons/buttons/calendarChevronRight.svg'
import { BaseButton } from '../../components'
import { CalendarDropdown } from './dropdown'
import { Text } from '../text/textStyles'

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`

const NavigationContainer = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 43px;
`

const DropdownContainer = styled.div`
    display: flex;
    gap: 4px;
`

const NavigationButton = styled(BaseButton)<{ hidden: boolean }>`
    opacity: ${(p) => (p.hidden ? '0' : '1')};
    pointer-events: ${(p) => (p.hidden ? 'none' : 'auto')};
`

const DayOfWeekContainer = styled.div`
    display: flex;
    justify-content: space-between;
`

const DayOfWeekHeader = styled(Text)`
    width: 60px;
    padding: 13px 0;
`

interface Props extends ReactDatePickerCustomHeaderProps {
    newDateSelected?: Date
}

export const CalendarHeader: FC<Props> = (props) => {
    const dayOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

    const getYears = (startYear: number, endYear: number) => {
        const years = []

        for (let year = startYear; year <= endYear; year++) {
            years.push(year.toString())
        }

        return years.reverse()
    }

    const currentYear = new Date().getFullYear()
    const years = getYears(1990, currentYear)

    const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ]

    const currentMonthIndex = new Date().getMonth()
    const currentMonth = months[currentMonthIndex]

    const [monthSelected, setMonthSelected] = useState(currentMonth)
    const [yearSelected, setYearSelected] = useState(currentYear)

    const decreaseMonth = () => {
        const newMonthIndex = months.indexOf(monthSelected) - 1

        if (newMonthIndex == -1) {
            setYearSelected(yearSelected - 1)
            setMonthSelected(months[months.length - 1])
        } else {
            setMonthSelected(months[newMonthIndex])
        }

        props.decreaseMonth()
    }

    const increaseMonth = () => {
        const newMonthIndex = months.indexOf(monthSelected) + 1

        if (newMonthIndex > months.length - 1) {
            setYearSelected(yearSelected + 1)
            setMonthSelected(months[0])
        } else {
            setMonthSelected(months[newMonthIndex])
        }

        props.increaseMonth()
    }

    useEffect(() => {
        if (props.newDateSelected) {
            const newMonthSelectedIndex = props.newDateSelected.getMonth()

            if (newMonthSelectedIndex != months.indexOf(monthSelected)) {
                setMonthSelected(months[newMonthSelectedIndex])
                props.changeMonth(newMonthSelectedIndex)
            }

            const newYearSelected = props.newDateSelected.getFullYear()

            if (newYearSelected != yearSelected) {
                setYearSelected(newYearSelected)
                props.changeYear(newYearSelected)
            }
        }
    }, [props.newDateSelected])

    return (
        <Container>
            <NavigationContainer>
                <NavigationButton
                    onClick={decreaseMonth}
                    hidden={props.prevMonthButtonDisabled}
                >
                    <LeftIcon />
                </NavigationButton>

                <DropdownContainer>
                    <CalendarDropdown
                        options={months}
                        defaultOption={monthSelected}
                        highlightedOption={currentMonth}
                        onSelect={(month) => {
                            setMonthSelected(month)
                            props.changeMonth(months.indexOf(month))
                        }}
                        width={171}
                    />

                    <CalendarDropdown
                        options={years}
                        defaultOption={yearSelected.toString()}
                        highlightedOption={currentYear.toString()}
                        onSelect={(year) => {
                            setYearSelected(Number(year))
                            props.changeYear(Number(year))
                        }}
                        width={105}
                    />
                </DropdownContainer>

                <NavigationButton
                    onClick={increaseMonth}
                    hidden={props.nextMonthButtonDisabled}
                >
                    <RightIcon />
                </NavigationButton>
            </NavigationContainer>

            <DayOfWeekContainer>
                {dayOfWeek.map((day) => (
                    <DayOfWeekHeader size="medium" $weight="medium">
                        {day}
                    </DayOfWeekHeader>
                ))}
            </DayOfWeekContainer>
        </Container>
    )
}
